/**
 * Fase 1F — AuthContext dual-mode.
 *
 * Modo legado (padrão): comportamento original preservado — usa
 * ``/api/auth/*`` e retorna diretamente ``user``.
 *
 * Modo global (``REACT_APP_PLATFORM_AUTH_ENABLED === "true"``): usa
 * ``/api/platform/auth/*`` sob cookies HttpOnly gerenciados pelo
 * backend. Não armazena tokens no navegador — apenas o estado
 * derivado (usuário local + tenant ativo).
 *
 * Adaptação: em modo global, ``user`` exposto ao restante do app
 * mantém a mesma forma do legado (``id, name, email, role, status``),
 * permitindo que Layout/ProtectedRoute e as telas operacionais
 * continuem funcionando SEM edição.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, formatApiError, setPlatformUnauthorizedHandler } from "../lib/api";
import { isPlatformAuthEnabled } from "../lib/flags";
import { setPlatformBootingFlag, setPlatformRefreshEndpoint } from "../lib/apiPlatform";

const AuthContext = createContext(null);

// Estados internos para o modo global.
export const AUTH_STATE = Object.freeze({
  LOADING: "loading",
  GUEST: "guest",
  NEEDS_TENANT: "needs_tenant",
  AUTHED: "authed",
  // Fase 1L.8C — sessão do Admin Console (sem tenant selecionado).
  AUTHED_ADMIN_ONLY: "authed_admin_only",
});

// Endpoints do Admin Console (relativos ao baseURL /api — o interceptor
// prefixa /platform automaticamente).
const ADMIN_CONSOLE_ENDPOINTS = Object.freeze({
  ENTER: "/auth/select-tenant/admin-console",
  ME: "/auth/admin-console/me",
  REFRESH: "/platform/auth/refresh/admin-console",
  EXIT: "/auth/refresh/admin-console/exit",
  LOGOUT: "/auth/refresh/admin-console/logout",
});
const TENANT_REFRESH_ENDPOINT = "/platform/auth/refresh";
const ADMIN_CONSOLE_GENERIC_ERROR =
  "Não foi possível acessar a Administração global. Tente novamente.";

/** Adapta ``local_user`` do backend global para o formato que as telas
 * legadas já esperam (``id``, ``name``, ``email``, ``role``, ``status``).
 * Nenhum campo sensível é copiado — ``tenant_db_name`` jamais chega ao
 * frontend, ``global_user_id``/``membership_id`` são descartados. */
function adaptLocalUser(local) {
  if (!local || typeof local !== "object") return null;
  return {
    id: local.local_user_id,
    name: local.name || "",
    email: local.email || "",
    role: local.role || "member",
    status: local.status || "active",
  };
}

function adaptTenant(t) {
  if (!t || typeof t !== "object") return null;
  return { tenant_id: t.tenant_id, name: t.name, slug: t.slug };
}

export function AuthProvider({ children }) {
  const platformMode = isPlatformAuthEnabled();

  // Estado unificado.
  const [state, setState] = useState(AUTH_STATE.LOADING);
  const [user, setUser] = useState(null); // legacy: null=loading, false=guest, obj=authed
  const [tenant, setTenant] = useState(null);
  const [pendingTenants, setPendingTenants] = useState([]); // apenas durante seleção
  const [error, setError] = useState("");
  const [platformAdmin, setPlatformAdmin] = useState(false); // Fase 1I.1
  // Fase 1L.8C — flag propagada pelo backend em /login e /select-tenant.
  const [adminConsoleAvailable, setAdminConsoleAvailable] = useState(false);
  const bootingRef = useRef(false);

  // Registra callback de 401 → volta ao login SEM manipular cookies.
  useEffect(() => {
    setPlatformUnauthorizedHandler(() => {
      setUser(false);
      setTenant(null);
      setPendingTenants([]);
      setState(AUTH_STATE.GUEST);
      setError(
        "Sua sessão expirou ou esta empresa não está disponível. Entre novamente."
      );
    });
    return () => setPlatformUnauthorizedHandler(null);
  }, []);

  // -------------------------------------------------------------------------
  // Boot: tenta identidade tenant e, se falhar em modo platform, faz
  // fallback para /auth/admin-console/me. Enquanto está em boot, o
  // handler de ``onUnauthorized`` fica silenciado para evitar flash de
  // GUEST antes do fallback (ver ``setPlatformBootingFlag``).
  // -------------------------------------------------------------------------
  const refresh = useCallback(async () => {
    if (!platformMode) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setState(data ? AUTH_STATE.AUTHED : AUTH_STATE.GUEST);
        return data;
      } catch {
        setUser(false);
        setTenant(null);
        setState(AUTH_STATE.GUEST);
        return null;
      }
    }
    bootingRef.current = true;
    setPlatformBootingFlag(true);
    setPlatformRefreshEndpoint(TENANT_REFRESH_ENDPOINT);
    try {
      try {
        const { data } = await api.get("/auth/me");
        if (data?.authenticated && data?.local_user && data?.tenant) {
          setUser(adaptLocalUser(data.local_user));
          setTenant(adaptTenant(data.tenant));
          setPendingTenants([]);
          setAdminConsoleAvailable(false);
          setState(AUTH_STATE.AUTHED);
          return data;
        }
      } catch { /* segue para fallback admin_console */ }
      // Fase 1L.8C — fallback: sessão de Admin Console sem tenant.
      setPlatformRefreshEndpoint(ADMIN_CONSOLE_ENDPOINTS.REFRESH);
      try {
        const { data } = await api.get(ADMIN_CONSOLE_ENDPOINTS.ME);
        if (data?.authenticated && data?.scope === "admin_console") {
          setUser({
            id: data.global_user_id,
            name: "",
            email: "",
            role: "platform_admin",
            status: "active",
          });
          setTenant(null);
          setPendingTenants([]);
          setAdminConsoleAvailable(true);
          setState(AUTH_STATE.AUTHED_ADMIN_ONLY);
          return data;
        }
      } catch { /* sem sessão válida */ }
      setPlatformRefreshEndpoint(TENANT_REFRESH_ENDPOINT);
      setUser(false);
      setTenant(null);
      setState(AUTH_STATE.GUEST);
      return null;
    } finally {
      bootingRef.current = false;
      setPlatformBootingFlag(false);
    }
  }, [platformMode]);

  // Boot inicial.
  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    refresh();
  }, [refresh]);

  // -------------------------------------------------------------------------
  // login
  // -------------------------------------------------------------------------
  const login = async (email, password) => {
    setError("");
    try {
      // NÃO fazer trim na senha — preservar exatamente como digitada.
      if (platformMode) {
        const { data } = await api.post("/auth/login", { email, password });
        if (data?.requires_tenant_selection) {
          setPendingTenants(Array.isArray(data.tenants) ? data.tenants : []);
          setAdminConsoleAvailable(!!data.admin_console_available);
          setState(AUTH_STATE.NEEDS_TENANT);
          return {
            needsTenant: true,
            tenants: data.tenants || [],
            adminConsoleAvailable: !!data.admin_console_available,
          };
        }
        // Se por algum motivo backend já autenticou (ex.: tenant único),
        // ainda assim exigimos passar pela seleção nesta fase.
        setPendingTenants(Array.isArray(data?.tenants) ? data.tenants : []);
        setAdminConsoleAvailable(!!data?.admin_console_available);
        setState(AUTH_STATE.NEEDS_TENANT);
        return {
          needsTenant: true,
          tenants: data?.tenants || [],
          adminConsoleAvailable: !!data?.admin_console_available,
        };
      }
      // legacy
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      setState(AUTH_STATE.AUTHED);
      return { needsTenant: false, user: data.user };
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (name, email, password) => {
    setError("");
    if (platformMode) {
      // Cadastro público não existe no modo global — mantido por
      // compatibilidade com a rota legada quando a flag está desativada.
      throw new Error("Cadastro público indisponível no modo multi-tenant.");
    }
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data.user);
      setState(AUTH_STATE.AUTHED);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  // -------------------------------------------------------------------------
  // selectTenant (apenas platform)
  // -------------------------------------------------------------------------
  const selectTenant = async (tenantId) => {
    setError("");
    if (!platformMode) throw new Error("Fluxo global indisponível.");
    if (!tenantId || typeof tenantId !== "string") {
      throw new Error("Selecione uma empresa.");
    }
    try {
      await api.post("/auth/select-tenant", { tenant_id: tenantId });
      await refresh();
      return true;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  // -------------------------------------------------------------------------
  // logout
  // -------------------------------------------------------------------------
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
    setTenant(null);
    setPendingTenants([]);
    setState(AUTH_STATE.GUEST);
  };

  // -------------------------------------------------------------------------
  // Cancela a seleção e volta ao login.
  // -------------------------------------------------------------------------
  const cancelTenantSelection = async () => {
    if (platformMode) {
      // Chama logout global para invalidar cookies pending — SEM manipular JS cookies.
      try { await api.post("/auth/logout"); } catch { /* ignore */ }
    }
    setPendingTenants([]);
    setUser(false);
    setTenant(null);
    setState(AUTH_STATE.GUEST);
  };

  // -------------------------------------------------------------------------
  // Fase 1I.0 — Troca de senha global. Após sucesso, força novo login.
  // -------------------------------------------------------------------------
  const changePassword = async ({ current_password, new_password, confirm_password }) => {
    if (!platformMode) throw new Error("Troca de senha global indisponível no modo legado.");
    await api.post("/auth/change-password", {
      current_password, new_password, confirm_password,
    });
    // Backend já limpou os 3 cookies globais. Zera estado local e redireciona.
    setPendingTenants([]); setUser(false); setTenant(null);
    setState(AUTH_STATE.GUEST);
  };

  // -------------------------------------------------------------------------
  // Fase 1I.0 — Listagem e troca de empresa sem novo login.
  // -------------------------------------------------------------------------
  const listAvailableTenants = async () => {
    if (!platformMode) return [];
    const { data } = await api.get("/auth/tenants");
    return Array.isArray(data?.tenants) ? data.tenants : [];
  };

  const switchTenant = async (tenant_id) => {
    if (!platformMode) throw new Error("Troca de empresa indisponível no modo legado.");
    const { data } = await api.post("/auth/switch-tenant", { tenant_id });
    if (data?.no_op) return { no_op: true, tenant };
    // Atualiza tenant no contexto; os cookies novos já vieram na resposta.
    setTenant({ tenant_id: data.tenant_id, name: data.name, slug: data.slug });
    return { no_op: false, tenant: { tenant_id: data.tenant_id, name: data.name, slug: data.slug } };
  };

  // -------------------------------------------------------------------------
  // Fase 1I.1 — Verifica se o usuário é ``platform_admin`` GLOBAL.
  // 403 apenas define ``platformAdmin=false``; sem toast e sem logout.
  // -------------------------------------------------------------------------
  const checkPlatformAdmin = useCallback(async () => {
    if (!platformMode) { setPlatformAdmin(false); return false; }
    try {
      const { data } = await api.get("/admin/access");
      const ok = !!data?.authorized;
      setPlatformAdmin(ok);
      return ok;
    } catch {
      setPlatformAdmin(false);
      return false;
    }
  }, [platformMode]);

  // Sempre que a autenticação estabilizar como AUTHED, revalida o papel
  // global. Em GUEST/loading, zera para false.
  useEffect(() => {
    if (state === AUTH_STATE.AUTHED) { checkPlatformAdmin(); }
    else { setPlatformAdmin(false); }
  }, [state, tenant?.tenant_id, checkPlatformAdmin]);

  // -------------------------------------------------------------------------
  // Fase 1L.8C — Admin Console (rotas globais sem tenant).
  // Nunca lê/escreve token no navegador — só HTTP calls contra o backend.
  // -------------------------------------------------------------------------
  const enterAdminConsole = async () => {
    if (!platformMode) throw new Error(ADMIN_CONSOLE_GENERIC_ERROR);
    try {
      await api.post(ADMIN_CONSOLE_ENDPOINTS.ENTER);
      // Consulta identidade — confirma a sessão e ativa o refresh admin.
      setPlatformRefreshEndpoint(ADMIN_CONSOLE_ENDPOINTS.REFRESH);
      const { data } = await api.get(ADMIN_CONSOLE_ENDPOINTS.ME);
      if (!data?.authenticated || data?.scope !== "admin_console") {
        setPlatformRefreshEndpoint(TENANT_REFRESH_ENDPOINT);
        throw new Error(ADMIN_CONSOLE_GENERIC_ERROR);
      }
      setUser({
        id: data.global_user_id,
        name: "",
        email: "",
        role: "platform_admin",
        status: "active",
      });
      setTenant(null);
      setPendingTenants([]);
      setState(AUTH_STATE.AUTHED_ADMIN_ONLY);
      return true;
    } catch (e) {
      setPlatformRefreshEndpoint(TENANT_REFRESH_ENDPOINT);
      throw new Error(ADMIN_CONSOLE_GENERIC_ERROR);
    }
  };

  const exitAdminConsole = async () => {
    if (!platformMode) throw new Error(ADMIN_CONSOLE_GENERIC_ERROR);
    try {
      const { data } = await api.post(ADMIN_CONSOLE_ENDPOINTS.EXIT);
      setPlatformRefreshEndpoint(TENANT_REFRESH_ENDPOINT);
      setUser(false);
      setTenant(null);
      setPendingTenants(Array.isArray(data?.tenants) ? data.tenants : []);
      setAdminConsoleAvailable(!!data?.admin_console_available);
      setState(AUTH_STATE.NEEDS_TENANT);
      return {
        tenants: data?.tenants || [],
        adminConsoleAvailable: !!data?.admin_console_available,
      };
    } catch (e) {
      throw new Error(ADMIN_CONSOLE_GENERIC_ERROR);
    }
  };

  const logoutAdminConsole = async () => {
    // Idempotente e tolerante — sempre reseta estado local.
    try { await api.post(ADMIN_CONSOLE_ENDPOINTS.LOGOUT); } catch { /* ignore */ }
    setPlatformRefreshEndpoint(TENANT_REFRESH_ENDPOINT);
    setUser(false);
    setTenant(null);
    setPendingTenants([]);
    setAdminConsoleAvailable(false);
    setState(AUTH_STATE.GUEST);
  };

  const value = {
    // legacy/compatibilidade
    user, error, login, register, logout, refresh, setUser,
    // platform-specific (ignorado em modo legado)
    state, tenant, pendingTenants,
    selectTenant, cancelTenantSelection,
    changePassword, listAvailableTenants, switchTenant,
    platformMode, platformAdmin, checkPlatformAdmin,
    // Admin Console (Fase 1L.8C)
    adminConsoleAvailable,
    enterAdminConsole, exitAdminConsole, logoutAdminConsole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve estar dentro de AuthProvider");
  return ctx;
}
